import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";
import { newsType } from "../../news.type";

export type Props = {
  /** The page shown, counting from 1 */
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

/** How many page numbers show at once, as many as the design draws. */
export const VISIBLE_PAGES = 7;
/** A number's place: 40px wide, 2px apart. */
const STEP = 42;

/**
 * Which page is the first of the numbers on show (counting from 1). The page
 * in view sits in the middle place, so in the middle pages the numbers move
 * under it; near either end the numbers stop and the page's place moves
 * instead. With seven or fewer pages every number shows and nothing moves.
 */
export function firstShown(page: number, totalPages: number): number {
  const middle = Math.ceil(VISIBLE_PAGES / 2);
  const last = Math.max(1, totalPages - VISIBLE_PAGES + 1);
  return Math.min(Math.max(1, page - middle + 1), last);
}

// The design's arrows are 20px in the lighter green. The shared button sizes
// any icon it is given at 16px, so these keep their own size.
const PreviousArrow = () => <ArrowLeft aria-hidden className="size-5 text-brand-600" strokeWidth={1.67} />;
const NextArrow = () => <ArrowRight aria-hidden className="size-5 text-brand-600" strokeWidth={1.67} />;

/**
 * Previous and Next at either end, the page numbers between, under a rule.
 *
 * On a phone the numbers give way to "Page 2 of 10", which fits beside the two
 * arrows where ten numbers would not.
 */
export function NewsPagination({ page, totalPages, onPageChange }: Props) {
  if (totalPages <= 1) return null;
  const isFirst = page <= 1;
  const isLast = page >= totalPages;
  const first = firstShown(page, totalPages);
  const places = Math.min(VISIBLE_PAGES, totalPages);
  // At either end the arrow that leads nowhere keeps its place and its look,
  // as the design draws Previous on the first page, but does nothing.
  const edge = "disabled:bg-transparent disabled:border-transparent disabled:text-brand-700 disabled:cursor-default";

  return (
    <nav
      aria-label="News pages"
      className="flex w-full items-center justify-between gap-5 border-t border-brand-700 pt-5"
      data-testid="news-pagination"
    >
      <div className="flex flex-1 justify-start">
        <Button
          variant="link-inline"
          iconComponent={PreviousArrow}
          onClick={() => onPageChange(page - 1)}
          disabled={isFirst}
          className={`gap-1 ${edge}`}
          typeClassName={newsType.pager}
          data-testid="news-pagination-previous"
        >
          Previous
        </Button>
      </div>

      {/* Every page's number on one strip, seen through a window of seven.
          The strip slides to keep the page in view in the middle place, and
          the yellow mark slides between places once an end is reached. */}
      <div
        className="relative hidden overflow-hidden md:block"
        style={{ width: places * STEP - 2 }}
        data-testid="news-pagination-numbers"
      >
        <span
          aria-hidden
          className="absolute top-0 left-0 size-10 rounded-full bg-brand-100 transition-transform duration-300 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(${(page - first) * STEP}px)` }}
          data-testid="news-pagination-mark"
        />
        <ol
          className="relative flex gap-0.5 transition-transform duration-300 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(${-(first - 1) * STEP}px)` }}
        >
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => {
            const isShown = number >= first && number < first + places;
            return (
              <li key={number} aria-hidden={isShown ? undefined : true}>
                <Button
                  variant="ghost"
                  onClick={() => onPageChange(number)}
                  aria-current={number === page ? "page" : undefined}
                  aria-label={`Page ${number}`}
                  tabIndex={isShown ? undefined : -1}
                  // A click does not take the focus, so no ring is left on a
                  // clicked number to ride away with the strip. The keyboard
                  // still focuses the numbers, with the ring.
                  onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                  // The mark under the strip is the page in view; a button's
                  // own ground stays clear so the two never disagree.
                  className="size-10 p-0 bg-transparent border-transparent hover:bg-transparent hover:border-transparent hover:underline"
                  typeClassName={`${newsType.pager} px-0`}
                >
                  {number}
                </Button>
              </li>
            );
          })}
        </ol>
      </div>

      <Type variant="caption" as="p" className={`${newsType.pager} md:hidden`}>
        Page {page} of {totalPages}
      </Type>

      <div className="flex flex-1 justify-end">
        <Button
          variant="link-inline"
          iconComponent={NextArrow}
          iconPosition="right"
          onClick={() => onPageChange(page + 1)}
          disabled={isLast}
          className={`gap-1 ${edge}`}
          typeClassName={newsType.pager}
          data-testid="news-pagination-next"
        >
          Next
        </Button>
      </div>
    </nav>
  );
}

export default NewsPagination;
